import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { downloadPdfBlob, renderPdfTemplateToBlob, printPdfBlob } from './pdfPrintingService';
import { loadPdfDocumentFromBlob } from './utils/loadPdfJs';
import './PdfPrintPreviewModal.css';

/** @typedef {{ templateId: string; props: Record<string, unknown>; title: string; onPrinted: () => void; onCancel: () => void }} PdfPrintPreviewSession */

const MIN_SCALE = 0.65;
const MAX_SCALE = 1;
const SCALE_STEP = 0.01;
const REGENERATE_DEBOUNCE_MS = 280;

function titleToFilename(title) {
    const base = String(title || '')
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase();
    return base ? `${base}.pdf` : 'sicily-document.pdf';
}

/**
 * @param {PdfPrintPreviewSession} props
 */
export function PdfPrintPreviewModal({
    templateId,
    props: templateProps,
    title,
    onPrinted,
    onCancel,
}) {
    const [scale, setScale] = useState(1);
    const [debouncedScale, setDebouncedScale] = useState(1);
    const [blob, setBlob] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [printing, setPrinting] = useState(false);
    const pagesRef = useRef(null);
    const renderTokenRef = useRef(0);

    useEffect(() => {
        document.getElementById('body')?.classList.add('modal-open');
        return () => {
            document.getElementById('body')?.classList.remove('modal-open');
        };
    }, []);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && !printing) onCancel();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onCancel, printing]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedScale(scale), REGENERATE_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [scale]);

    useEffect(() => {
        let cancelled = false;
        const token = ++renderTokenRef.current;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const nextBlob = await renderPdfTemplateToBlob(templateId, templateProps, {
                    scale: debouncedScale,
                });
                if (cancelled || token !== renderTokenRef.current) return;

                const pdfDoc = await loadPdfDocumentFromBlob(nextBlob);
                if (cancelled || token !== renderTokenRef.current) return;

                const numPages = pdfDoc.numPages;
                setPageCount(numPages);
                setBlob(nextBlob);

                const container = pagesRef.current;
                if (!container) return;
                container.innerHTML = '';

                for (let i = 1; i <= numPages; i += 1) {
                    const page = await pdfDoc.getPage(i);
                    if (cancelled || token !== renderTokenRef.current) return;

                    const viewport = page.getViewport({ scale: 1.15 });
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.className = 'pdf-print-preview-page-canvas';
                    await page.render({ canvasContext: ctx, viewport }).promise;
                    container.appendChild(canvas);
                }
            } catch (e) {
                if (!cancelled && token === renderTokenRef.current) {
                    setError(e?.message || 'Could not generate preview.');
                }
            } finally {
                if (!cancelled && token === renderTokenRef.current) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [templateId, templateProps, debouncedScale]);

    const scalePercent = Math.round(scale * 100);

    const handlePrint = useCallback(async () => {
        if (!blob || printing) return;
        setPrinting(true);
        try {
            await printPdfBlob(blob);
            onPrinted();
        } catch (e) {
            setError(e?.message || 'Could not open print dialog.');
            setPrinting(false);
        }
    }, [blob, onPrinted, printing]);

    const handleDownload = useCallback(() => {
        if (!blob || loading || printing) return;
        downloadPdfBlob(blob, titleToFilename(title));
    }, [blob, loading, printing, title]);

    return (
        <div
            className="pdf-print-preview-backdrop"
            role="presentation"
            onClick={printing ? undefined : onCancel}
        >
            <div
                className="pdf-print-preview-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pdf-print-preview-title"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="pdf-print-preview-header">
                    <div>
                        <h2 id="pdf-print-preview-title" className="pdf-print-preview-title">
                            {title}
                        </h2>
                        <p className="pdf-print-preview-hint">
                            Adjust scale so billing and delivery fit on one page, then print or
                            download. Browser print scaling cannot move content between PDF pages.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pdf-print-preview-close"
                        onClick={onCancel}
                        disabled={printing}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </header>

                <div className="pdf-print-preview-controls">
                    <label htmlFor="pdf-print-scale" className="pdf-print-preview-scale-label">
                        Document scale: <strong>{scalePercent}%</strong>
                        {pageCount > 0 ? (
                            <span className="pdf-print-preview-pages">
                                · {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                            </span>
                        ) : null}
                    </label>
                    <input
                        id="pdf-print-scale"
                        type="range"
                        min={MIN_SCALE}
                        max={MAX_SCALE}
                        step={SCALE_STEP}
                        value={scale}
                        onChange={(e) => setScale(Number(e.target.value))}
                        disabled={printing}
                    />
                    <div className="pdf-print-preview-scale-ends">
                        <span>{Math.round(MIN_SCALE * 100)}%</span>
                        <span>{Math.round(MAX_SCALE * 100)}%</span>
                    </div>
                </div>

                <div className="pdf-print-preview-body">
                    {loading ? (
                        <div className="pdf-print-preview-loading">
                            <ClipLoader color="#c4603c" size={32} />
                            <span>Updating preview…</span>
                        </div>
                    ) : null}
                    {error ? <div className="pdf-print-preview-error">{error}</div> : null}
                    <div ref={pagesRef} className="pdf-print-preview-pages-wrap" />
                </div>

                <footer className="pdf-print-preview-footer">
                    <button
                        type="button"
                        className="pdf-print-preview-btn pdf-print-preview-btn--ghost"
                        onClick={onCancel}
                        disabled={printing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="pdf-print-preview-btn pdf-print-preview-btn--ghost"
                        onClick={handleDownload}
                        disabled={!blob || loading || printing}
                    >
                        Download PDF
                    </button>
                    <button
                        type="button"
                        className="pdf-print-preview-btn"
                        onClick={handlePrint}
                        disabled={!blob || loading || printing}
                    >
                        {printing ? 'Opening print…' : 'Print'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
